import { Suspense, lazy, useCallback, useEffect, useState } from 'react';
import type { ComponentType, JSX, LazyExoticComponent } from 'react';
import { CanvasBoundary, ScreenLayout, SkeletonBlock } from '@taki/ui';
import { useCadreSurface } from '@taki/ui/cadre-canvas';
import type { TakiSdk } from '@taki/sdk';
import type { AppDeclaree, ProprietesDeSurface } from './app-declaree.js';
import { IconeDeFiche } from './icone.js';
import { chatterActifSurLeJeu, useSuiviDuGabarit } from './suivi.js';
import { useAppRuntime } from './runtime.js';
import { creerCacheDeSurfaces } from './surface-paresseuse.js';
/** App Canvas : le cadre et la surface sont deux responsabilites separees.
 * Le cadre lit la declaration unique et rend les composants natifs ; la surface
 * fournit leurs donnees et commandes par l'API commune aux portes et prototypes.
 */
const surfaceEnCache = creerCacheDeSurfaces<LazyExoticComponent<ComponentType<ProprietesDeSurface>>>();

function surfaceDe(app: AppDeclaree, essai: number): LazyExoticComponent<ComponentType<ProprietesDeSurface>> | null {
  const charger = app.appCanvas?.charger;
  if (charger === undefined) return null;
  return surfaceEnCache(app, essai, () => lazy(charger));
}

/** Ce que la zone 5 montre pendant que le morceau de la surface arrive. */
function AttenteDeSurface(): JSX.Element {
  return (
    <div className="flex h-full flex-col gap-3 p-6" aria-busy="true">
      <SkeletonBlock w="40%" h="1.25rem" />
      <SkeletonBlock h="100%" rounded="var(--radius-md)" className="min-h-[12rem] flex-1" />
    </div>
  );
}

type FicheMontree = { family: string; recordId: string; titre: string };

/**
 * LA FICHE POSEE PAR LA SURFACE, si son suivi est allume : d'abord la regle de
 * l'ecran declare sur la declaration de l'app (`chatterActifSurLeJeu`), puis
 * la reponse du serveur pour toutes les apps de l'org. Un SDK sans
 * `chatterActif` (branche plus ancienne) : la regle locale seule.
 */
function useFicheSuivie(sdk: TakiSdk, app: AppDeclaree, fiche: FicheMontree | null): FicheMontree | null {
  const localement = fiche !== null && chatterActifSurLeJeu(app.champs, { family: fiche.family });
  const [reponse, setReponse] = useState<{ cle: string; actif: boolean } | null>(null);
  const cle = fiche === null ? null : `${fiche.family}/${fiche.recordId}`;
  const family = fiche?.family;
  const recordId = fiche?.recordId;
  useEffect(() => {
    if (!localement || cle === null || family === undefined || recordId === undefined) return;
    const conversations = sdk.conversations as Partial<Pick<TakiSdk['conversations'], 'chatterActif'>>;
    if (typeof conversations.chatterActif !== 'function') {
      setReponse({ cle, actif: true });
      return;
    }
    let vivant = true;
    void conversations.chatterActif({ family, recordId }).then(
      (actif) => { if (vivant) setReponse({ cle, actif }); },
      () => { if (vivant) setReponse({ cle, actif: true }); },
    );
    return () => { vivant = false; };
  }, [sdk, localement, cle, family, recordId]);
  if (fiche === null || !localement) return null;
  return reponse?.cle === cle && reponse.actif ? fiche : null;
}

export function EcranAppCanvas({ app }: { app: AppDeclaree }): JSX.Element {
  const { sdk } = useAppRuntime();
  const canvas = { level: 'app' as const, ...app.fiche.canvas?.app };
  const [recherche, setRecherche] = useState('');
  const { api, props } = useCadreSurface(app.id, canvas, recherche);
  const [ficheMontree, setFicheMontree] = useState<FicheMontree | null>(null);
  const poserFiche = useCallback((suivante: FicheMontree | null) => {
    setFicheMontree((avant) => (avant?.recordId === suivante?.recordId && avant?.titre === suivante?.titre ? avant : suivante));
  }, []);
  /* LE SUIVI ETEINT PAR UNE BASE (`jeux[].chatter: false`) l'est ici aussi.
     L'ecran declare le lit dans SA declaration (`chatterActifSurLeJeu`) ; une
     App Canvas n'en a pas (aucun jeu declare), et la fiche qu'elle pose peut
     etre celle d'une base qu'une AUTRE app de l'org eteint. Seul le serveur
     connait les declarations de toutes les apps installees : on lui demande
     (`sdk.conversations.chatterActif`, la meme lecture que sa garde), une fois
     par fiche posee. Tant qu'il n'a pas repondu, pas de bouton : un bouton qui
     disparait apres coup aurait promis un suivi qui n'existe pas. */
  const ficheSuivie = useFicheSuivie(sdk, app, ficheMontree);
  const suivi = useSuiviDuGabarit(sdk, ficheSuivie !== null ? { ...ficheSuivie, appId: app.id, doorId: app.id } : null, { fil: false });
  const [essai, setEssai] = useState(0);
  const reessayer = useCallback(() => setEssai((n) => n + 1), []);
  const Surface = surfaceDe(app, essai);

  return (
    <ScreenLayout
      canvas={canvas}
      appName={app.fiche.menu.label}
      appIcon={<IconeDeFiche nom={app.fiche.icon} size={18} />}
      title={app.fiche.name}
      searchValue={recherche}
      onSearchChange={setRecherche}
      suivi={suivi}
      {...props}
    >
      <CanvasBoundary surfaceId={app.id} onRetry={reessayer}>
        {Surface === null ? null : (
          <Suspense fallback={<AttenteDeSurface />}>
            <Surface appId={app.id} {...api} poserFiche={poserFiche} />
          </Suspense>
        )}
      </CanvasBoundary>
    </ScreenLayout>
  );
}

export default EcranAppCanvas;
